import Button from 'bf-library/Button';
import Input from 'bf-library/Input';
import Modal from 'bf-library/Modal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage, injectIntl, intlShape } from 'react-intl';

import useOnClickOutside from '../../helpers/useOnClickOutside';
import useWindowSize from '../../helpers/useWindowSize';
import SearchBarItem from './SearchBarItem';
import { useHotel } from 'State/HotelsContext';
import { useWidgetContext } from 'State/WidgetContext';
import { API } from 'Services';

function SpecialCodes(props) {
    const {
        hasPromoCode, hasCouponCode, intl, couponVoucherTerm, coupon, promoCodePlaceholder, onChangePromoCode, applyCoupon, channelId,
        promotion,
    } = props;

    const widgetContext = useWidgetContext();
    const id = widgetContext.id;

    const [ isSpecialCodesOpen, setIsSpecialCodesOpen ] = useState(false);
    const [ couponCode, setCouponCode ] = useState(coupon);
    const [ couponCodeError, setCouponCodeError ] = useState(false);
    const [ couponCodeErrorMessage, setCouponCodeErrorMessage ] = useState('');
    const [ isCouponValid, setIsCouponValid ] = useState(false);
    const [ promoCode, setPromoCode ] = useState(promotion);
    const [ codeApplied, setCodeApplied ] = useState('');

    const windowSize = useWindowSize();
    const isMobile = windowSize.width <= 768;
    const ref = useRef();

    useEffect(() => {
        setCodeApplied(specialCodeSearchbarValue(coupon, promotion));

        if (!coupon) {
            setIsCouponValid(false);
            setCouponCode('');
        }
    }, [ coupon, promotion ]);

    function specialCodeSearchbarValue(couponValue, promotionalCodeValue) {
        if (couponValue && promotionalCodeValue) {
            return <FormattedMessage id="bf.specialCodesApplied" defaultMessage="bf.specialCodesApplied" />;
        }
        if (couponValue && !promotionalCodeValue) {
            return couponValue;
        }
        if (!couponValue && promotionalCodeValue) {
            return promotionalCodeValue;
        }
        return '';
    }

    const inputsPlaceholder = intl.formatMessage({ id: 'bf.enterCode', defaultMessage: 'bf.enterCode' });

    function getSpecialCodesLabel() {
        if (hasPromoCode && !hasCouponCode) {
            return promoCodePlaceholder || <FormattedMessage id="bf.havePromocode" defaultMessage="bf.havePromocode" />;
        }
        if (!hasPromoCode && hasCouponCode) {
            return <FormattedMessage id="bf.couponCode" defaultMessage="bf.couponCode" values={{ couponTerm: couponVoucherTerm }} />;
        }
        if (hasPromoCode && hasCouponCode) {
            return <FormattedMessage id="bf.specialCodes" defaultMessage="bf.specialCodes" />;
        }
        return <FormattedMessage id="bf.havePromocode" defaultMessage="bf.havePromocode" />;
    }

    function getSpecialCodesPlaceholder() {
        if (hasPromoCode && hasCouponCode) {
            return <FormattedMessage id="bf.enterYourCodes" defaultMessage="bf.enterYourCodes" />;
        }
        return <FormattedMessage id="bf.enterYourCode" defaultMessage="bf.enterYourCode" />;
    }
    async function validateCoupon() {
        const { result: { valid }, warnings } = await API.validateCoupon({ couponCode, channelId });
        if (valid) {
            applyCoupon(couponCode);
            setIsCouponValid(true);
            setCouponCodeError(false);
            setIsSpecialCodesOpen(true);
        } else {
            setCouponCodeError(true);
            setIsCouponValid(false);
            setCouponCodeErrorMessage(warnings[0].message);
            setIsSpecialCodesOpen(true);
        }
    }

    async function handleSpecialCodes() {
        onChangePromoCode(id, promoCode);
        if (couponCode) {
            await validateCoupon();
        } else {
            setIsSpecialCodesOpen(false);
            setCouponCodeError(false);
            setIsCouponValid(false);
        }
    }

    useOnClickOutside(ref, () => !isMobile && setIsSpecialCodesOpen(false));

    const specialCodeLabel = getSpecialCodesLabel();
    const specialCodePlaceholder = getSpecialCodesPlaceholder();

    const specialCodesSearchbarItem = (
        <SearchBarItem
            className="kbe-special-codes-search-bar-item search-bar-input-wrapper-custom-class"
            label={specialCodeLabel}
            onClick={() => setIsSpecialCodesOpen(!isSpecialCodesOpen)}
            content={codeApplied || specialCodePlaceholder}
            hasDropdown
        />
    );

    const specialCodesContent = (
        <div className="kbe-special-codes-popup-content">
            {hasPromoCode ? (
                <Input
                    label={promoCodePlaceholder || <FormattedMessage id="bf.havePromocode" defaultMessage="bf.havePromocode" />}
                    placeholder={inputsPlaceholder}
                    onChange={event => setPromoCode(event.target.value)}
                    value={promoCode}
                />
            ) : null}
            {hasCouponCode ? (
                <>
                    <Input
                        label={couponVoucherTerm}
                        placeholder={inputsPlaceholder}
                        onChange={event => setCouponCode(event.target.value)}
                        value={couponCode}
                    />
                    {isCouponValid ? (
                        <div className="kbe-coupon-form-feedback-message kbe-coupon-form-feedback-message-success">
                            <FormattedHTMLMessage
                                id="bf.couponTerminologyCodeIsValid"
                                defaultMessage="bf.couponTerminologyCodeIsValid"
                                values={{ couponVoucherTerm }}
                            />
                        </div>
                    ) : null}
                    {couponCodeError ? (
                        <div className="kbe-coupon-form-feedback-message kbe-coupon-form-feedback-message-error">
                            {couponCodeErrorMessage}
                        </div>
                    ) : null}
                </>
            ) : null}
        </div>
    );

    const applySpecialCodesButton = (
        <Button
            className="kbe-special-codes-apply-button"
            primary
            content={<FormattedMessage id="bf.applyCode" defaultMessage="bf.applyCode" />}
            onClick={() => handleSpecialCodes()}
        />
    );

    return (
        <div className="kbe-special-codes-wrapper" ref={ref}>
            {isMobile
                ? (
                    <Modal
                        trigger={specialCodesSearchbarItem}
                        title={specialCodeLabel}
                        content={specialCodesContent}
                        footer={(
                            <div className="kbe-special-codes-modal-footer">
                                {applySpecialCodesButton}
                            </div>
                        )}
                        externalOpen={isSpecialCodesOpen}
                        onClose={() => setIsSpecialCodesOpen(false)}
                        onOpen={() => setIsSpecialCodesOpen(true)}
                    />
                )
                : (
                    <>
                        {specialCodesSearchbarItem}
                        {isSpecialCodesOpen ? (
                            <div className="kbe-special-codes-popup popup-content">
                                {specialCodesContent}
                                <div className="kbe-special-codes-popup-apply-button-wrapper">
                                    {applySpecialCodesButton}
                                </div>
                            </div>
                        ) : null}
                    </>
                )}
        </div>
    );
}

SpecialCodes.defaultProps = {
    hasPromoCode         : false,
    hasCouponCode        : false,
    couponVoucherTerm    : '',
    coupon               : '',
    promoCodePlaceholder : '',
};

SpecialCodes.propTypes = {
    hasPromoCode         : PropTypes.bool,
    hasCouponCode        : PropTypes.bool,
    intl                 : intlShape.isRequired,
    couponVoucherTerm    : PropTypes.string,
    coupon               : PropTypes.string,
    promoCodePlaceholder : PropTypes.string,
    onChangePromoCode    : PropTypes.func.isRequired,
    applyCoupon          : PropTypes.func.isRequired,
    channelId            : PropTypes.string.isRequired,
    promotion            : PropTypes.string.isRequired,
};

export default injectIntl(SpecialCodes);
// i18nLabel[bf.enterYourCode]
// i18nLabel[bf.enterYourCodes]
